import { useEffect, useRef, useState } from "react";
import { Television, ArrowUpRight } from "@phosphor-icons/react";
import { trackAffiliateClick } from "@/lib/api";

// Official JustWatch widget (https://apis.justwatch.com/docs/widget/configuration/).
// Renders live streaming / rent / buy offers for a title, keyed off its title + year.
const JW_API_KEY = process.env.REACT_APP_JUSTWATCH_API_KEY;
const JW_SCRIPT_SRC = "https://widget.justwatch.com/justwatch_widget.js";

export function JustWatchWidget({ title, year, videoId, isShow = false, tmdbId }) {
  const wrapperRef = useRef(null);
  // loading → ready (offers rendered) → empty (no offers / zero-height box)
  const [status, setStatus] = useState("loading");

  // ReelVault is an SPA, so the widget script must be re-injected whenever the user
  // navigates to a different movie — otherwise the library won't re-scan the new
  // [data-jw-widget] container. We drop the old script and append a fresh one on change.
  useEffect(() => {
    if (!title) return undefined;
    setStatus("loading");

    const prev = document.getElementById("justwatch-widget-script");
    if (prev) prev.remove();

    const script = document.createElement("script");
    script.id = "justwatch-widget-script";
    script.async = true;
    script.src = JW_SCRIPT_SRC;
    document.body.appendChild(script);

    // Unreleased titles (e.g. upcoming blockbusters) return no offers, leaving a big empty
    // dark box. Poll the container; if it never renders real content, collapse to a fallback.
    let tries = 0;
    const interval = setInterval(() => {
      tries += 1;
      // The JW script swaps our placeholder div for its own iframe, so measure that iframe.
      const frame = wrapperRef.current?.querySelector("iframe.jw-widget-iframe, iframe");
      if (frame && frame.offsetHeight > 48) {
        setStatus("ready");
        clearInterval(interval);
        return;
      }
      if (tries >= 12) {
        setStatus("empty");
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      script.remove();
    };
  }, [title, year, isShow, tmdbId]);

  if (!title) return null;

  return (
    <section
      data-testid="where-to-watch"
      className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5"
    >
      <h3 className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 mb-5">
        <Television size={16} weight="duotone" className="text-amber" />
        Where to Watch
      </h3>

      {/* Official JustWatch widget container — collapsed (hidden) when it has no offers. */}
      <div ref={wrapperRef} data-testid="justwatch-widget" style={{ display: status === "empty" ? "none" : "block" }}>
        <div
          key={`${title}-${year || ""}-${isShow ? "show" : "movie"}`}
          data-jw-widget
          data-api-key={JW_API_KEY}
          data-object-type={isShow ? "show" : "movie"}
          data-language="en"
          data-country="US"
          {...(tmdbId ? { "data-id": String(tmdbId), "data-id-type": "tmdb" } : { "data-title": title })}
          {...(year ? { "data-year": String(year) } : {})}
          data-theme="dark"
        />
      </div>

      {status === "empty" && (
        <p data-testid="jw-empty-state" className="font-mono text-[12px] text-white/40">
          Streaming offers not available yet.
        </p>
      )}

      {/* Mandatory JustWatch attribution (kept legible on our dark theme). */}
      <div className="mt-4 flex items-center">
        <a
          data-original="https://www.justwatch.com"
          href="https://www.justwatch.com/us"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="jw-powered-by"
          onClick={() => trackAffiliateClick({ provider: "justwatch", video_id: videoId, title })}
          className="rv-cta-glow rv-cta-glow--sky group inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-300/[0.06] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sky-200"
        >
          Powered by JustWatch
          <ArrowUpRight size={12} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <span className="ml-4 flex items-center gap-1.5 font-mono text-[10px] text-white/45">
          Source:
          <a
            data-original="https://www.justwatch.com"
            href="https://www.justwatch.com/us"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jw-source-link"
            onClick={() => trackAffiliateClick({ provider: "justwatch", video_id: videoId, title })}
            className="inline-flex items-center opacity-80 hover:opacity-100 transition-opacity"
          >
            <img alt="JustWatch" height="10" src="https://widget.justwatch.com/assets/JW_logo_color_10px.svg" style={{ height: 10 }} />
          </a>
        </span>
      </div>
    </section>
  );
}
